import { GET_RECOMMENDATIONS } from '../../constants/ActionTypes';
import { getCookie } from '../../helpers/getCookie';

const RECOM_SIZE = 6;
const FETCH_TIMEOUT = 11000; // 11 seconds
const RECOM_KEY = '_rec';
const RECOM_EXPIRE_KEY = `${RECOM_KEY}exp`;
const ANONYMOUS_SUFFIX = `base`;
const RECOM_EXPIRE_HOUR = 5;

const fetchUserRecommendationsRequest = () => ({
  type: `${GET_RECOMMENDATIONS}_PENDING`,
});

const fetchUserRecommendationsSuccess = (items) => ({
  type: `${GET_RECOMMENDATIONS}_SUCCESS`,
  payload: items,
});

const fetchUserRecommendationsFailure = (error) => ({
  type: `${GET_RECOMMENDATIONS}_FAIL`,
  payload: error,
});

// Define o horário para expiração da recom do LS
// Sempre a 1 da manhã
function setExpirationTime(userId) {
  // Lê a data atual
  let expDay = new Date();

  // Define a data para o último segundo do dia atual para usuário logado
  if (userId !== ANONYMOUS_SUFFIX) {
    return expDay.setHours(23, 59, 59, 999);
  }

  if (expDay.getHours() >= RECOM_EXPIRE_HOUR) {
    expDay = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
  }

  return expDay.setHours(RECOM_EXPIRE_HOUR, 0, 0);
}

function saveRecommendations(items, userId) {
  const key = `${RECOM_KEY}-${userId}`;

  // Salva a recom
  localStorage.setItem(key, JSON.stringify(items.slice(0, RECOM_SIZE)));

  // Salva a data de expiração
  localStorage.setItem(
    `${RECOM_EXPIRE_KEY}-${userId}`,
    JSON.stringify(setExpirationTime(userId)),
  );

  return items;
}

// Verifica se os dados estão expirados com base na data de expiracao passada.
const isDataExpired = (date) => {
  const now = new Date();
  const expirationDate = new Date(date);

  return now > expirationDate;
};

const mergeRecommendationsResponse = (responses, blackList) => {
  const validResponses = responses.filter(
    (response) => response?.output?.services?.length > 0,
  );

  const intercalatedResponses = [];

  while (intercalatedResponses.length < RECOM_SIZE) {
    let added = false;

    for (const response of validResponses) {
      const items = response.output.services.filter(
        (item) =>
          !blackList.includes(item.id) &&
          !intercalatedResponses.some((addedItem) => addedItem.id === item.id),
      );

      if (items[0]) {
        intercalatedResponses.push({
          ...items[0],
          title: items[0]['short-title'] || items[0].title,
        });
        added = true;

        if (intercalatedResponses.length === RECOM_SIZE) {
          break;
        }
      }
    }

    // Se não foi possível adicionar mais itens, interrompe o loop
    if (!added) {
      break;
    }
  }

  // Retorna as recomendações disponíveis, mesmo que sejam menos que RECOM_SIZE
  return intercalatedResponses;
};

// Retorna apenas os 3 ultimos servicos acessados nas ultimas 24h
function recentHistoryIDs(history) {
  // Obtem a data e hora atual em milisegundos
  const dataAtual = Date.now();
  // Subtrai 24 horas em milissegundos
  const limiteInferior = dataAtual - 24 * 60 * 60 * 1000;
  // Filtra os tres ultimos servicos que foram inseridos no historico nas ultimas 24 horas
  // const servicosRecentes = history.filter((item) => item.date > limiteInferior);
  const servicosRecentes = history
    .filter((item) => item.date > limiteInferior)
    .slice(0, 3)
    .map((item) => item.id)
    .join(','); // unir os ids em uma unica string, separados por virgula
  return servicosRecentes;
}

const getAnonymousRecommendations = async (
  recomUrl,
  history,
  blackList,
  dispatch,
) => {
  if (!history || !history.length) {
    saveRecommendations([], ANONYMOUS_SUFFIX);
    dispatch(fetchUserRecommendationsSuccess([]));
    return [];
  }

  const ids = history.map((item) => item.id);

  try {
    // Um único controller para todas as requisições
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    // Função para criar uma URL com parâmetros
    const createUrl = (id) => {
      const url = new URL(`${recomUrl}/servicos/recomendacoes`);

      url.searchParams.append('size', blackList.length + RECOM_SIZE);
      url.searchParams.append('services', id);
      url.searchParams.append('exclude_input', 'false');
      return url;
    };

    // Cria promessas para cada ID
    const promises = ids.slice(0, 3).map((id) =>
      fetch(createUrl(id), {
        method: 'GET',
        signal: controller.signal,
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`Falha na requisição: ${response.status}`);
        }
        return response.json();
      }),
    );

    // Aguarda todas as promessas - se uma falhar, todas são canceladas
    const responses = await Promise.all(promises).catch((error) => {
      controller.abort(); // Cancela todas as requisições pendentes
      throw error; // Repassa o erro
    });

    clearTimeout(timeoutId);

    // Processa as respostas e mescla os resultados
    const recommendationLists = mergeRecommendationsResponse(
      responses,
      blackList,
    );

    saveRecommendations(recommendationLists, ANONYMOUS_SUFFIX);
    dispatch(fetchUserRecommendationsSuccess(recommendationLists));
  } catch (error) {
    dispatch(fetchUserRecommendationsFailure(error.toString()));
  }
};

export const getUserRecommendations =
  (userId, recomUrl, history, blackList) => async (dispatch) => {
    dispatch(fetchUserRecommendationsRequest());

    try {
      const csrfToken = getCookie('XSRF-TOKEN');

      const cacheId = userId || ANONYMOUS_SUFFIX;

      const localStorageKey = `${RECOM_KEY}-${cacheId}`;
      const localData = JSON.parse(localStorage.getItem(localStorageKey));
      const expTime = localStorage.getItem(`${RECOM_EXPIRE_KEY}-${cacheId}`);

      if (localData && expTime && !isDataExpired(expTime)) {
        dispatch(fetchUserRecommendationsSuccess(localData));
        return;
      }

      if (!userId || !csrfToken) {
        await getAnonymousRecommendations(
          recomUrl,
          history,
          blackList,
          dispatch,
        );

        return;
      }

      const recentIds = recentHistoryIDs(history);

      const url = new URL(
        `/govbrportal/api/recommendations`,
        window.location.origin,
      );

      url.searchParams.append('size', blackList.length + RECOM_SIZE);
      url.searchParams.append('services', recentIds);
      url.searchParams.append('shallow', 'false');

      const controller = new AbortController();

      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'X-XSRF-TOKEN': csrfToken,
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        // dispatch(
        //   fetchUserRecommendationsFailure(
        //     data?.errors?.[0]?.code || 'UNEXPECTED_ERROR',
        //   ),
        // );

        throw new Error(`Erro na resposta da API: ${response.statusText}`);
      }

      const services = data?.output?.services;

      const filteredServices = services
        .filter((srv) => !blackList.includes(srv.id))
        .slice(0, RECOM_SIZE);

      saveRecommendations(filteredServices, userId);
      dispatch(fetchUserRecommendationsSuccess(filteredServices));
      return filteredServices;
    } catch (error) {
      dispatch(fetchUserRecommendationsFailure(error.toString()));
    }
  };
